// src/pages/Expenses/ExpensesDashboard.tsx
//
// Expenses admin screen — one list view (no Today/History split, unlike
// visitors, since GET /expenses has no unscoped "today" endpoint and always
// requires session_id).
//
// Row actions: view (GET /:id), edit (PUT /:id), approve/reject
// (PUT /:id/approve | /:id/reject, pending only), delete (DELETE /:id).

import React, { useCallback, useEffect, useRef, useState, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import { GlobalDateField } from '../../components/GlobalComponents/GlobalDateField';
import { GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import ExpenseFormModal from './parts/ExpenseFormModal';
import ViewExpenseModal from './parts/ViewExpenseModal';
import ExpensesSkeletonLoader from '../../skeletons/ExpensesSkeletonLoader';
import {
  ApiExpense,
  CATEGORY_SUGGESTIONS,
  ExpensePagination,
  ExpenseRecord,
  ExpenseStatus,
  ExpenseStatusCounts,
  PAYMENT_MODE_OPTIONS,
  STATUS_META,
  formatCurrency,
  formatExpenseDate,
  mapApiExpense,
  paymentModeLabel,
  toDateParam,
} from './parts/types';

type StatusFilter = 'all' | ExpenseStatus;

const EMPTY_COUNTS: ExpenseStatusCounts = { pending: 0, approved: 0, rejected: 0 };

const STATUS_FILTERS: { value: StatusFilter; label: string; color: string }[] = [
  { value: 'all', label: 'All', color: '#4169E1' },
  { value: 'pending', label: 'Pending', color: '#f59e0b' },
  { value: 'approved', label: 'Approved', color: '#16a34a' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444' },
];

const ExpensesDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Every expense endpoint that lists or summarises is session-scoped and
  // rejects a missing session_id, so nothing here fetches until it's ready.
  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [counts, setCounts] = useState<ExpenseStatusCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [paymentMode, setPaymentMode] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<(Date | null)[] | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<ExpensePagination>({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 1,
  });

  const [viewId, setViewId] = useState<string | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingExpense, setEditingExpense] = useState<ExpenseRecord | null>(null);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  const startDate = dateRange?.[0] ?? null;
  const endDate = dateRange?.[1] ?? null;

  const fetchExpenses = useCallback(async () => {
    if (!currentSessionId) {
      setExpenses([]);
      return;
    }

    setLoading(true);
    try {
      const params: any = { page, limit: pageSize, session_id: currentSessionId };
      if (status !== 'all') params.status = status;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (category) params.category = category;
      if (paymentMode) params.payment_mode = paymentMode;
      if (startDate) params.start_date = toDateParam(startDate);
      if (endDate) params.end_date = toDateParam(endDate);

      const res = await apiClient.get('api/expenses', { params });
      const data = res.data;

      if (data.status === 'success') {
        const rows: ApiExpense[] = data.data || [];
        setExpenses(rows.map(mapApiExpense));
        setPagination(data.pagination);
        setCounts(data.counts ?? EMPTY_COUNTS);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Not loaded',
          detail: data.message || 'The expense list could not be loaded.',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Could not reach the expense list.',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    currentSessionId,
    page,
    pageSize,
    status,
    debouncedSearch,
    category,
    paymentMode,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const refreshAll = () => {
    fetchExpenses();
  };

  const resetToFirstPage = () => setPage(1);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const handleApprove = async (expense: ExpenseRecord) => {
    setBusyRowId(expense.id);
    try {
      const res = await apiClient.put(`api/expenses/${expense.id}/approve`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Approved',
          detail: `${formatCurrency(expense.amount)} for ${expense.payeeName} approved.`,
          life: 4000,
        });
        refreshAll();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The expense could not be approved.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while approving.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const handleReject = async (expense: ExpenseRecord) => {
    const reason = window.prompt(`Reason for rejecting ${expense.payeeName}'s expense:`);
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Reason required',
        detail: 'A rejection reason is required.',
        life: 4000,
      });
      return;
    }

    setBusyRowId(expense.id);
    try {
      const res = await apiClient.put(`api/expenses/${expense.id}/reject`, { reason: reason.trim() });
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Rejected',
          detail: `${expense.payeeName}'s expense was rejected.`,
          life: 4000,
        });
        refreshAll();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The expense could not be rejected.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while rejecting.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const handleDelete = async (expense: ExpenseRecord) => {
    const confirmed = window.confirm(
      `Delete this ${formatCurrency(expense.amount)} expense for ${expense.payeeName}? The receipt is removed too and this can't be undone.`
    );
    if (!confirmed) return;

    setBusyRowId(expense.id);
    try {
      const res = await apiClient.delete(`api/expenses/${expense.id}`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: 'The expense record is gone.',
          life: 4000,
        });
        refreshAll();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The expense could not be deleted.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while deleting.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingExpense(null);
    setIsFormOpen(true);
  };

  const openEdit = (expense: ExpenseRecord) => {
    setFormMode('edit');
    setEditingExpense(expense);
    setIsViewOpen(false);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingExpense(null);
    if (formMode === 'create') setPage(1);
    refreshAll();
  };

  // ── column templates ─────────────────────────────────────────────────────

  const serialTemplate = (_: ExpenseRecord, options: ColumnBodyOptions): JSX.Element => (
    <div style={serialStyle}>{options.rowIndex + (page - 1) * pageSize + 1}</div>
  );

  const payeeTemplate = (row: ExpenseRecord): JSX.Element => (
    <div style={{ minWidth: 0 }}>
      <div style={nameStyle}>{row.payeeName}</div>
      {row.payeeContact && <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.payeeContact}</div>}
    </div>
  );

  const categoryTemplate = (row: ExpenseRecord): JSX.Element => (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 8,
        background: 'rgba(65,105,225,.08)',
        color: '#1e40af',
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {row.category}
    </span>
  );

  const amountTemplate = (row: ExpenseRecord): JSX.Element => (
    <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', fontFamily: 'monospace' }}>
      {formatCurrency(row.amount)}
    </div>
  );

  const paymentTemplate = (row: ExpenseRecord): JSX.Element => (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
        {paymentModeLabel(row.paymentMode)}
      </div>
      {row.transactionRef && (
        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{row.transactionRef}</div>
      )}
    </div>
  );

  const descriptionTemplate = (row: ExpenseRecord): JSX.Element => (
    <div style={ellipsisStyle} title={row.description}>
      {row.description}
    </div>
  );

  const dateTemplate = (row: ExpenseRecord): JSX.Element => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
      {formatExpenseDate(row.expenseDate)}
    </div>
  );

  const statusTemplate = (row: ExpenseRecord): JSX.Element => {
    const meta = STATUS_META[row.status];
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '4px 10px',
          borderRadius: 20,
          fontSize: 11.5,
          fontWeight: 700,
          color: meta.color,
          background: meta.bg,
          border: `1px solid ${meta.border}`,
          whiteSpace: 'nowrap',
        }}
      >
        <i className={meta.icon} style={{ fontSize: 11 }} />
        {meta.label}
      </span>
    );
  };

  const actionTemplate = (row: ExpenseRecord): JSX.Element => {
    const busy = busyRowId === row.id;
    return (
      <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-rounded"
          tooltip="View"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#60a5fa', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => {
            setViewId(row.id);
            setIsViewOpen(true);
          }}
        />
        {row.status === 'pending' && (
          <>
            <Button
              icon="pi pi-check"
              className="p-button-text p-button-rounded"
              tooltip="Approve"
              tooltipOptions={{ position: 'top' }}
              style={{ color: '#16a34a', width: 32, height: 32 }}
              disabled={busy}
              onClick={() => handleApprove(row)}
            />
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded"
              tooltip="Reject"
              tooltipOptions={{ position: 'top' }}
              style={{ color: '#ef4444', width: 32, height: 32 }}
              disabled={busy}
              onClick={() => handleReject(row)}
            />
          </>
        )}
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-rounded"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#3b82f6', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => openEdit(row)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-rounded"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#ef4444', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => handleDelete(row)}
        />
      </div>
    );
  };

  // ── empty state copy ─────────────────────────────────────────────────────

  const emptyState = (() => {
    if (!currentSessionId)
      return {
        icon: 'pi pi-exclamation-circle',
        title: 'No active session',
        message: 'Expenses are scoped to a session and none is set for this school yet.',
      };
    if (debouncedSearch.trim())
      return {
        icon: 'pi pi-search',
        title: 'No matches',
        message: `Nothing matched "${debouncedSearch}". Try a payee name, description, or transaction reference.`,
      };
    if (status !== 'all' || category || paymentMode || dateRange)
      return {
        icon: 'pi pi-filter',
        title: 'Nothing matches these filters',
        message: 'Clear a filter or widen the date range to see more expenses.',
      };
    return {
      icon: 'pi pi-wallet',
      title: 'No expenses recorded',
      message: 'Record an expense and it will show up here.',
    };
  })();

  const hasFilters = !!(category || paymentMode || dateRange);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:14px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .expense-toolbar { background:white; border-radius:16px; padding:14px 18px; margin-bottom:16px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); }
          .expense-search { display:flex; align-items:center; gap:10px; flex:1; min-width:240px; background:#f8fafc; border:1px solid #e5e7eb; border-radius:12px; padding:9px 14px; transition:border-color .2s; }
          .expense-search:focus-within { border-color:rgba(59,130,246,.45); background:white; }
          .expense-search input { flex:1; border:none; outline:none; background:transparent; font-size:13px; font-weight:500; color:#1f2937; }
          .expense-search input::placeholder { color:#9ca3af; font-weight:400; }
          .expense-clear { background:none; border:none; cursor:pointer; color:#9ca3af; display:flex; padding:2px; }
          .expense-clear:hover { color:#6b7280; }
          .status-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid #e5e7eb; background:white; color:#4b5563; transition:all .15s; }
          .status-chip:hover { border-color:#93c5fd; }
          .status-chip .chip-count { font-size:11px; font-weight:700; padding:1px 7px; border-radius:10px; background:#f1f5f9; }
        `}</style>

        <GlobalHeaderTitle
          title="Expenses"
          subtitle={`${pagination.total_items} records`}
          icon="pi pi-wallet"
          buttons={[
            {
              label: 'Refresh',
              icon: 'pi pi-refresh',
              onClick: refreshAll,
              outlined: true,
              disabled: loading,
              style: { borderWidth: '2px' },
            },
            {
              label: 'Record expense',
              icon: 'pi pi-plus',
              onClick: openCreate,
            },
          ]}
        />

        {/* ── Toolbar ── */}
        <div className="expense-toolbar">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="expense-search">
              <i className="pi pi-search" style={{ fontSize: 14, color: '#3b82f6' }} />
              <input
                type="text"
                placeholder="Search by payee, description, or transaction reference"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {searchQuery && (
                <button className="expense-clear" onClick={() => handleSearchChange('')}>
                  <i className="pi pi-times" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
            {STATUS_FILTERS.map((opt) => {
              const active = status === opt.value;
              const count = opt.value === 'all' ? pagination.total_items : counts[opt.value as ExpenseStatus];
              return (
                <button
                  key={opt.value}
                  className="status-chip"
                  onClick={() => {
                    setStatus(opt.value);
                    resetToFirstPage();
                  }}
                  style={active ? { background: opt.color, borderColor: opt.color, color: 'white' } : undefined}
                >
                  {opt.label}
                  <span
                    className="chip-count"
                    style={active ? { background: 'rgba(255,255,255,.25)', color: 'white' } : { color: opt.color }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid #f1f5f9',
            }}
          >
            <div style={{ minWidth: 240, maxWidth: 320, flex: '0 1 300px' }}>
              <GlobalDateField
                label="Date range"
                value={dateRange}
                onChange={(val) => {
                  setDateRange(val as (Date | null)[] | null);
                  resetToFirstPage();
                }}
                selectionMode="range"
                placeholder="Select date range"
                dateFormat="dd M yy"
                icon="pi pi-calendar"
                showButtonBar
                fullWidth
              />
            </div>

            <div style={{ minWidth: 160, maxWidth: 190, flex: '0 1 170px' }}>
              <GlobalDropdownField
                label="Category"
                value={category}
                onChange={(val) => {
                  setCategory(val);
                  resetToFirstPage();
                }}
                options={CATEGORY_SUGGESTIONS.map((opt) => ({ label: opt, value: opt }))}
                placeholder="All categories"
                showClear
                fullWidth
              />
            </div>

            <div style={{ minWidth: 160, maxWidth: 190, flex: '0 1 170px' }}>
              <GlobalDropdownField
                label="Payment mode"
                value={paymentMode}
                onChange={(val) => {
                  setPaymentMode(val);
                  resetToFirstPage();
                }}
                options={PAYMENT_MODE_OPTIONS}
                placeholder="Any mode"
                showClear
                fullWidth
              />
            </div>

            {hasFilters && (
              <Button
                label="Clear filters"
                icon="pi pi-filter-slash"
                className="p-button-text p-button-sm"
                style={{ marginBottom: 2 }}
                onClick={() => {
                  setDateRange(null);
                  setCategory(null);
                  setPaymentMode(null);
                  resetToFirstPage();
                }}
              />
            )}
          </div>
        </div>

        {/* ── Table / Skeleton ── */}
        {loading ? (
          <ExpensesSkeletonLoader />
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {expenses.length > 0 && (
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, pagination.total_items)} of {pagination.total_items} records
                </span>
                <div
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'rgba(65,105,225,.08)',
                    border: '1px solid rgba(65,105,225,.15)',
                    fontSize: 13,
                    color: '#4169E1',
                    fontWeight: 600,
                  }}
                >
                  Page {pagination.current_page} of {pagination.total_pages}
                </div>
              </div>
            )}

            <DataTable
              value={expenses}
              loading={false}
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 360,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                    }}
                  >
                    <i className={emptyState.icon} style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3 style={{ margin: '0 0 8px', color: '#1e40af', fontSize: 20, fontWeight: 700, textAlign: 'center' }}>
                    {emptyState.title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {emptyState.message}
                  </p>
                </div>
              }
            >
              <Column header="#" body={serialTemplate} style={{ width: 52 }} />
              <Column header="Payee" body={payeeTemplate} />
              <Column header="Category" body={categoryTemplate} style={{ width: 140 }} />
              <Column header="Amount" body={amountTemplate} style={{ width: 120 }} />
              <Column header="Payment" body={paymentTemplate} style={{ width: 150 }} />
              <Column header="Description" body={descriptionTemplate} />
              <Column header="Date" body={dateTemplate} style={{ width: 110 }} />
              <Column header="Status" body={statusTemplate} style={{ width: 120 }} />
              <Column header="Actions" body={actionTemplate} style={{ width: 175 }} />
            </DataTable>

            {pagination.total_pages > 1 && (
              <CustomPaginator
                page={pagination.current_page}
                totalPages={pagination.total_pages}
                totalRecords={pagination.total_items}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}
      </div>

      <ViewExpenseModal
        open={isViewOpen}
        expenseId={viewId}
        onClose={() => setIsViewOpen(false)}
        onChanged={refreshAll}
        onEdit={openEdit}
      />

      <ExpenseFormModal
        open={isFormOpen}
        mode={formMode}
        initialRecord={editingExpense}
        onClose={() => {
          setIsFormOpen(false);
          setEditingExpense(null);
        }}
        onSuccess={handleFormSuccess}
      />
    </>
  );
};

const serialStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  background: '#f9fafb',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 12,
  color: '#6b7280',
  margin: '0 auto',
};

const nameStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  color: '#1f2937',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const ellipsisStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#4b5563',
  maxWidth: 220,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default ExpensesDashboard;